/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';
import { styled } from '@compiled/react';
import type { PreloadedQuery } from 'react-relay';
import { Stack } from '@atlaskit/primitives';
import { cssMap, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import BreadcrumbRenderer from '@atlassian/jira-breadcrumbs/src/breadcrumb-renderer/index.tsx';
import PageHeader from '@atlassian/jira-common-components-page-header/src/index.tsx';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { useIntl } from '@atlassian/jira-intl';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import type { viewBacklogCustomFiltersQuery } from '@atlassian/jira-relay/src/__generated__/viewBacklogCustomFiltersQuery.graphql';
import type { viewBacklogVersionFilterRelayQuery } from '@atlassian/jira-relay/src/__generated__/viewBacklogVersionFilterRelayQuery.graphql';
import { useShowQuickFilters } from '@atlassian/jira-software-view-settings/src/controllers/index.tsx';
import type { backlogFilterRefinementQuery } from '@atlassian/jira-relay/src/__generated__/backlogFilterRefinementQuery.graphql.ts';
import { useBacklogSelector } from '../../state/index.tsx';
import type { State } from '../../state/reducers/types.tsx';
import { getBoardType, getIsCompanyManaged } from '../../state/selectors/app/index.tsx';
import { canEditBoardConfig } from '../../state/selectors/permissions/index.tsx';
import { ActionsBar } from './actions-bar/index.tsx';
import FilterSelectionBarContainer from './controls-bar/filter-selection-bar/index.tsx';
import ControlsBar from './controls-bar/index.tsx';
import messages from './messages.tsx';

export type Props = {
	customFiltersQueryRef?: PreloadedQuery<viewBacklogCustomFiltersQuery> | null;
	versionFilterRelayQueryRef?: PreloadedQuery<viewBacklogVersionFilterRelayQuery> | null;
	backlogFilterRefinementQueryRef?: PreloadedQuery<backlogFilterRefinementQuery> | null;
};

const styles = cssMap({
	root: {
		paddingTop: token('space.250'),
		paddingBottom: token('space.250'),
		gap: token('space.100'),
		display: 'flex',
		flexWrap: 'wrap-reverse',
		justifyContent: 'space-between',
		// media query md breakpoint
		'@media (min-width: 64rem)': {
			flexWrap: 'nowrap',
			gap: token('space.300'),
		},
	},
	actions: {
		alignItems: 'flex-start',
		marginLeft: 'auto',
		display: 'flex',
		justifyContent: 'flex-end',
		flex: '1 1 auto',
		// media query md breakpoint
		'@media (min-width: 64rem)': {
			flex: '0 1 auto',
		},
	},
});

export const getIsMenuVisible = (state: State) =>
	canEditBoardConfig(state) || getIsCompanyManaged(state);

function HeaderNew(props: Props) {
	const { customFiltersQueryRef, versionFilterRelayQueryRef, backlogFilterRefinementQueryRef } =
		props;
	const isMenuVisible = useBacklogSelector(getIsMenuVisible);
	const isFilterSelectionBarOpen = useShowQuickFilters();

	return (
		<div css={[styles.root]}>
			<Stack>
				{/* @ts-expect-error remove expect error on cleanup of backlog-filter-bar-controls-refactor */}
				<ControlsBar
					customFiltersQueryRef={customFiltersQueryRef}
					versionFilterRelayQueryRef={versionFilterRelayQueryRef}
					backlogFilterRefinementQueryRef={backlogFilterRefinementQueryRef}
				/>
				{isFilterSelectionBarOpen && <FilterSelectionBarContainer />}
			</Stack>
			<div css={[styles.actions]}>
				<ActionsBar isMenuVisible={isMenuVisible} />
			</div>
		</div>
	);
}

function HeaderOld(props: Props) {
	const { customFiltersQueryRef, versionFilterRelayQueryRef, backlogFilterRefinementQueryRef } =
		props;
	const isMenuVisible = useBacklogSelector(getIsMenuVisible);
	const boardType = useBacklogSelector(getBoardType);
	const { formatMessage } = useIntl();

	return (
		<PageHeader
			truncateTitle
			breadcrumbs={breadcrumbs}
			bottomBar={
				// @ts-expect-error remove expect error on cleanup of backlog-filter-bar-controls-refactor
				<ControlsBar
					customFiltersQueryRef={customFiltersQueryRef}
					versionFilterRelayQueryRef={versionFilterRelayQueryRef}
					backlogFilterRefinementQueryRef={backlogFilterRefinementQueryRef}
				/>
			}
			actions={<ActionsBar isMenuVisible={isMenuVisible} boardType={boardType} />}
		>
			<HeadingStyle>
				<TruncatingTextStyle>{formatMessage(messages.pageTitle)}</TruncatingTextStyle>
			</HeadingStyle>
		</PageHeader>
	);
}

export default componentWithCondition(() => getWillShowNav4(), HeaderNew, HeaderOld);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const BreadcrumbContainer = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'&.fullscreen': {
		display: 'none',
	},
});

const breadcrumbs = (
	<BreadcrumbContainer data-fullscreen-id="fullscreen-backlog-breadcrumbs">
		<BreadcrumbRenderer />
	</BreadcrumbContainer>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeadingStyle = styled.div({
	display: 'flex',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TruncatingTextStyle = styled.div({
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	flex: '0 1 auto',
});
